// Pure SVG-string builder for one scope's KJP chart (INFS-1283 export).
//
// This is the single place the chart's markup gets assembled. KjpChart.vue
// renders this exact string on screen (via v-html); the Excel export
// rasterises this exact string to a PNG. Neither one builds its own markup —
// two parallel builders would let the on-screen chart and the exported image
// drift apart from each other, which is the one thing this file exists to
// prevent.
//
// Self-contained by construction, because the export rasterises this string
// with no page around it to inherit from:
//   - an explicit generic `font-family` on the root <svg> (canvas would
//     otherwise fall back to the platform default serif face);
//   - colour via `fill`/`stroke` attributes only, never CSS
//     `background-color` or any external reference.
//
// No Vue, no DOM — buildChartSvg can run in a plain Node script the same way
// scripts/verify-kjp.mjs exercises src/kjp.js.

import { round2 } from '@/kjp'
import { KJP_CSS_COLORS } from '@/components/kjp/colors'
import { KJP_CHART_TITLES, KJP_CHART_SERIES } from '@/components/kjp/chartSeries'
import { buildChartGeometry, axisTicks, absentSeriesKeys } from '@/components/kjp/chartGeometry'

// Fixed pixel plot box the SVG is built around. Exported so KjpChart.vue's
// own `geometry` computed (used only to work out which series the chart
// couldn't draw, for the warning banner) reads the same plot box rather than
// keeping a second copy of these numbers that could drift from this file's.
export const PLOT = { left: 60, top: 46, width: 700, height: 300 }
const TITLE_Y = 22
const MONTH_LABEL_Y = PLOT.top + PLOT.height + 16
const LEGEND_TOP = PLOT.top + PLOT.height + 34
const LEGEND_ROW_H = 18
const VB_WIDTH = PLOT.left + PLOT.width + 80
const VB_HEIGHT = LEGEND_TOP + LEGEND_ROW_H * 2 + 14

export const CHART_VB = { width: VB_WIDTH, height: VB_HEIGHT }

function esc(s) {
  return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
}

// round2 before formatting, same rule as KjpTable.vue's fmt and KjpChart.vue's
// old inline copy — plain toLocaleString on a raw float would show a binary-
// rounding artefact the workbook doesn't have.
function fmt(v) {
  return round2(v).toLocaleString('en-MY', { minimumFractionDigits: 0, maximumFractionDigits: 2 })
}

// Builds the full self-contained `<svg>...</svg>` markup for one scope's
// chart from a built KJP model. Returns '' for a model with nothing to draw
// (no months) rather than an empty/degenerate SVG.
export function buildChartSvg(model, scope) {
  const scopeKey = scope || (model && model.scope)
  const title = KJP_CHART_TITLES[scopeKey] || ''
  const seriesDefs = KJP_CHART_SERIES[scopeKey] || []
  const months = (model && model.months) || []

  // Fix 4 (INFS-1283): this docstring promised '' for a model with nothing
  // to draw, but nothing below ever checked for it — buildChartSvg(null, ...)
  // or a { months: [] } model built ~1,400 characters of framed, seriesless
  // SVG (title, axes, gridlines, legend text) instead. Not reachable today —
  // the export's own writeScopeSheet early-returns before ever calling this
  // for an empty/failed model, and KjpChart.vue only renders once a model
  // exists — but leaving the guard unimplemented invites a future caller to
  // write `const svg = buildChartSvg(...); if (!svg) return;` and get a
  // degenerate empty-framed chart embedded instead of nothing at all.
  if (!months.length) return ''

  const textColor = KJP_CSS_COLORS.text

  const geometry = buildChartGeometry({
    seriesDefs,
    projections: (model && model.projections) || {},
    monthCount: months.length,
    plot: PLOT,
  })

  const barSeries = geometry.series
    .filter((s) => s.type === 'bar')
    .map((s) => ({ color: s.color, rects: s.bars.filter(Boolean) }))
  const lineSeries = geometry.series.filter((s) => s.type === 'line')
  const absentKeys = absentSeriesKeys(geometry.series)

  const leftTicks = axisTicks(geometry.axes.left.max, 4)
    .map((value) => ({ value, y: geometry.axes.left.yScale(value) }))
  const rightAxis = geometry.axes.right
  const rightTicks = rightAxis
    ? axisTicks(rightAxis.max, 4).map((value) => ({ value, y: rightAxis.yScale(value) }))
    : []

  const legendItems = seriesDefs.map((s, i) => ({
    ...s,
    absent: absentKeys.has(s.key),
    x: PLOT.left + (i % 2) * (PLOT.width / 2),
    y: LEGEND_TOP + Math.floor(i / 2) * LEGEND_ROW_H,
  }))

  const parts = []

  // font-family="sans-serif" (a CSS generic family, not a named font) is the
  // one declaration every descendant text element inherits — the export has
  // no page CSS around it, and canvas cannot see inherited styles anyway.
  parts.push(
    `<svg xmlns="http://www.w3.org/2000/svg" width="${VB_WIDTH}" height="${VB_HEIGHT}" ` +
      `viewBox="0 0 ${VB_WIDTH} ${VB_HEIGHT}" font-family="sans-serif">`
  )

  // Plot canvas: the same white spreadsheet surface as KjpTable.vue, drawn as
  // a real SVG fill (not CSS background-color) so it survives both printing
  // and rasterisation.
  parts.push(`<rect x="0" y="0" width="${VB_WIDTH}" height="${VB_HEIGHT}" fill="#fff" />`)

  parts.push(
    `<text x="${VB_WIDTH / 2}" y="${TITLE_Y}" text-anchor="middle" font-weight="bold" ` +
      `font-size="13" fill="${textColor}">${esc(title)}</text>`
  )

  // Y-axis gridlines + left tick labels (the bar axis in dual-axis mode, the
  // one shared axis otherwise), formatted with round2 like every other
  // figure in the KJP tables so the chart and the table never disagree.
  leftTicks.forEach((t) => {
    parts.push(
      `<line x1="${PLOT.left}" x2="${PLOT.left + PLOT.width}" y1="${t.y}" y2="${t.y}" ` +
        `stroke="#e0e0e0" stroke-width="1" />` +
        `<text x="${PLOT.left - 8}" y="${t.y + 3}" text-anchor="end" font-size="10" ` +
        `fill="${textColor}">${esc(fmt(t.value))}</text>`
    )
  })

  // Right axis (R01 only — its two line series scale against this instead of
  // the left/bar axis; see chartGeometry.js). No gridlines of its own: the
  // tick fractions are identical to the left axis's.
  if (rightTicks.length) {
    rightTicks.forEach((t) => {
      parts.push(
        `<text x="${PLOT.left + PLOT.width + 8}" y="${t.y + 3}" text-anchor="start" ` +
          `font-size="10" fill="${textColor}">${esc(fmt(t.value))}</text>`
      )
    })
    parts.push(
      `<line x1="${PLOT.left + PLOT.width}" x2="${PLOT.left + PLOT.width}" ` +
        `y1="${PLOT.top}" y2="${PLOT.top + PLOT.height}" stroke="${textColor}" stroke-width="1" />`
    )
  }

  parts.push(
    `<line x1="${PLOT.left}" x2="${PLOT.left}" y1="${PLOT.top}" y2="${PLOT.top + PLOT.height}" ` +
      `stroke="${textColor}" stroke-width="1" />`
  )
  parts.push(
    `<line x1="${PLOT.left}" x2="${PLOT.left + PLOT.width}" y1="${PLOT.top + PLOT.height}" ` +
      `y2="${PLOT.top + PLOT.height}" stroke="${textColor}" stroke-width="1" />`
  )

  // Bars. A month with no measurement anywhere has no rect at all (see
  // barSeries above, which already stripped the nulls barRects returns) —
  // never a zero-height bar sitting on the baseline.
  barSeries.forEach((series) => {
    series.rects.forEach((r) => {
      parts.push(`<rect x="${r.x}" y="${r.y}" width="${r.width}" height="${r.height}" fill="${series.color}" />`)
    })
  })

  // Lines. Each series may render as more than one path — buildChartGeometry
  // already split it into separate runs around any unmeasured month, so a run
  // boundary is a real gap in the drawing, never a bridge across missing data.
  lineSeries.forEach((series) => {
    series.runs.forEach((d) => {
      parts.push(`<path d="${d}" fill="none" stroke="${series.color}" stroke-width="2" />`)
    })
  })

  // X-axis month labels.
  months.forEach((m, i) => {
    parts.push(
      `<text x="${geometry.centers[i]}" y="${MONTH_LABEL_Y}" text-anchor="middle" font-size="10" ` +
        `fill="${textColor}">${esc(m.label)}</text>`
    )
  })

  // Legend. A series absent from the chart itself (unmeasured in every
  // month) still gets a legend row, but greyed and annotated so the legend
  // can never imply something is plotted when it isn't.
  legendItems.forEach((item) => {
    const color = item.absent ? '#ccc' : item.color

    if (item.type === 'bar') {
      parts.push(`<rect x="${item.x}" y="${item.y - 10}" width="12" height="12" fill="${color}" />`)
    } else {
      parts.push(
        `<line x1="${item.x}" x2="${item.x + 12}" y1="${item.y - 4}" y2="${item.y - 4}" ` +
          `stroke="${color}" stroke-width="3" />`
      )
    }

    parts.push(
      `<text x="${item.x + 18}" y="${item.y}" font-size="10.5" fill="${item.absent ? '#999' : textColor}">` +
        `${esc(item.label)}${item.absent ? ' (not shown)' : ''}</text>`
    )
  })

  parts.push('</svg>')

  return parts.join('')
}
